import { styled } from '@mui/material/styles';
import { PRODUCT_ESIM_TABLE_CONFIGURATION } from '../Components/models/configurations';
import { Outlet } from 'react-router';
import { ConfigurableTable } from 'common/components/ConfigurableTable/ConfigurableTable';
import { TextCell } from 'common/components/ConfigurableTable/Components/Cells/TextCell';
import { SuspenseLoadingView } from 'common/components/SuspenseLoadingView';
import { ContentBox } from 'common/components/AppLayout/ContentBox';
import { TableSkeleton } from 'common/components/Table/TableSkeleton';
import {
  TableLayoutActions,
  TableLayoutTable,
} from 'common/components/Layouts/TableLayout/TableLayout';
import { RefetchQueryButton } from 'common/components/Queries/RefetchQueryButton';

import { eSimProductsQueries } from '../utils/esimProductsQueries';
import { ColumnHeaders } from 'src/common/components/ConfigurableTable/Components/Rows/ColumnHeaders';
import { GridRow } from 'src/common/components/ConfigurableTable/Components/Rows/GridRow';
import { esimPageFilterAtom, esimRowsPerPageFilterAtom } from 'src/atoms/filters/esim-products';
import { useInventoryTablePagination } from 'src/common/components/Inventory/hooks/useInventoryTablePagination';
import { useESimProducts } from './hooks/useESimProducts';
import { ESim } from './models/esim';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { DetailsPanel } from 'src/common/components/ConfigurableTable/Components/Containers/DetailsPanel';
import { ESimProfiles } from './Profiles/ESimProfiles';
import { useESimTableActions } from './hooks/useESimTableActions';
import { ESimEIDLinkCell } from './cells/ESimEIDLinkCell';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';

const CustumizedConfigurationTable = styled(ConfigurableTable)(({ theme }) => ({
  '& .MuiDataGrid-overlayWrapper': {
    borderLeft: `1px solid ${theme.palette.border} !important`,
    borderRight: `1px solid ${theme.palette.border} !important`,
  },
  '& .MuiDataGrid-cell': {
    borderTop: 'none',
  },
  '& .MuiDataGrid-cell--pinnedRight': {
    right: '0 !important',
  },
  '& .MuiDataGrid-columnHeader[data-field="rSimStatus"] .MuiTypography-text3': {
    textTransform: 'none',
  },
  '& .MuiDataGrid-footerContainer': {
    border: `1px solid ${theme.palette.border}`,
    borderTop: 'none',
    overflow: 'hidden',
    borderBottomLeftRadius: DEFAULT_BORDER_RADIUS,
    borderBottomRightRadius: DEFAULT_BORDER_RADIUS,
  },
  '& .MuiDataGrid-filler': {
    visibility: 'hidden',
  },
}));

const ESIM_CELL_CONFIGURATION = {
  eid: ESimEIDLinkCell,
  default: ({
    value,
    subValue,
  }: {
    field: string;
    value?: string;
    subValue?: string;
    colDef: any;
  }) => {
    return <TextCell value={value} subValue={subValue} />;
  },
};

const getDetailPanelContent = (props: GridRowParams<ESim>) => {
  return (
    <DetailsPanel>
      <ESimProfiles eid={props.row.eid} />
    </DetailsPanel>
  );
};
const getDetailPanelHeight = () => 'auto' as const;

export const ESimTableContent = () => {
  const {
    queryResult: { data },
  } = useESimProducts();

  const { rowsPerPage, page, onPaginationChange } = useInventoryTablePagination({
    rowsPerPageFilterAtom: esimRowsPerPageFilterAtom,
    pageFilterAtom: esimPageFilterAtom,
  });

  const actions = useESimTableActions();

  return (
    <CustumizedConfigurationTable
      getRowHeight={() => 52}
      disableColumnReorder
      data-testid="esims inventory table"
      configuration={PRODUCT_ESIM_TABLE_CONFIGURATION}
      cellConfiguration={ESIM_CELL_CONFIGURATION}
      getRowId={({ eid }: ESim) => eid}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      rows={data.items}
      totalNumberOfItems={data.totalNumberOfItems}
      onPaginationChange={onPaginationChange}
      page={page}
      pageSize={rowsPerPage}
      slots={{
        columnHeaders: ColumnHeaders,
        row: GridRow,
      }}
      actions={actions}
      fillerWidth={1414}
    />
  );
};

export const ESimTable = () => {
  return (
    <>
      <TableLayoutActions showBorder>
        <RefetchQueryButton queryKey={eSimProductsQueries.all} />
      </TableLayoutActions>

      <TableLayoutTable>
        <SuspenseLoadingView
          loadFallback={
            <ContentBox>
              <TableSkeleton showStartSkeleton numberOfRows={20} />
            </ContentBox>
          }
          queryKey={eSimProductsQueries.eSims}
        >
          <ESimTableContent />
          <Outlet />
        </SuspenseLoadingView>
      </TableLayoutTable>
    </>
  );
};
